import React, { useState, useEffect } from 'react'
import {
  KTNDetails,
  KTNDetailsStatusEnum,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import PageContent from '@components/layout/PageContent'
import { HeadTitle } from '@components/layout/HeadTitle'
import { PageHeading, CardHeading } from '@components/layout/Heading'
import { Card, Flex, Text, Link, Alert } from '@clear/design-system'
import { Form, Row } from '@components/layout/Form'
import { StatusCardDetails } from '../status/StatusCardDetails'
import dateToString from '@utils/dateToString'
import { getDisplayName } from '@utils/displayName'
import NextLink from 'next/link'
import router from 'next/router'
import { ContinueButton } from '@components/FormButtons'
import { TSA_PRECHECK_BRAND } from '@api/constants'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { getXCLEARCorrelationInfo } from '@api/utils'

interface KtnCardProps {
  data: KTNDetails
}

const KtnCard: React.FC<KtnCardProps> = ({ data }) => {
  const displayName = getDisplayName(data.name)

  return (
    <PageContent>
      <HeadTitle
        title={`What is my KTN? | ${TSA_PRECHECK_BRAND} Known Traveler Number Look Up`}
        description={`If you are a ${TSA_PRECHECK_BRAND} member, you can look up your known traveler number online. Find your KTN here.`}
      />
      <PageHeading>Look Up KTN</PageHeading>
      <Card width="full" data-cy="ktn-card">
        <Flex flexDirection="column">
          <Form>
            <Row alignItems="center">
              <CardHeading>{displayName}</CardHeading>
            </Row>
            <CardDetails data={data} />
          </Form>
        </Flex>
      </Card>
    </PageContent>
  )
}

function CardDetails({ data }: KtnCardProps) {
  if (data.provider !== 'CLEAR') {
    const providerText = data.provider || 'another enrollment provider'
    const otherProviderNotice = `Your Known Traveler Number (KTN) was assigned by ${providerText}. Please visit their enrollment website to retrieve your KTN.`

    if (data.status === KTNDetailsStatusEnum.ELIGIBLEFORRENEWAL) {
      return (
        <NonClearRenewalEligible otherProviderNotice={otherProviderNotice} />
      )
    }
    return (
      <NonClearRenewalIneligible otherProviderNotice={otherProviderNotice} />
    )
  } else {
    return <ClearEnrollmentOrRenewal data={data} />
  }
}

function ClearEnrollmentOrRenewal({ data }: KtnCardProps) {
  const [clearKtnDescription, setClearKtnDescription] = useState(null)
  // For enrollments that are:
  //  1. In the CLEAR system
  //  2. IN_PROGRESS
  //  3. new (not renewal)
  //  the backend does not send the KTN.
  const isClearInProgressRenewal =
    data.provider === 'CLEAR' && !!data.ktn?.length
  const inProgressMessage = 'Check the status of your current application'

  useEffect(() => {
    if (data.status === KTNDetailsStatusEnum.INPROGRESS) {
      setClearKtnDescription(
        (
          <Row mx={[2, 6]} data-testid="ktn-card-in-progress">
            <Text
              variant="body"
              textAlign="center"
              color="neutralsTextGray"
              margin="auto"
              data-testid={
                isClearInProgressRenewal
                  ? 'in-progress-renewal-text'
                  : 'in-progress-enrollment-text'
              }
            >
              {isClearInProgressRenewal
                ? `Your renewal is in progress. ${inProgressMessage}`
                : inProgressMessage}{' '}
              <NextLink href="/status" passHref>
                <Link>here.</Link>
              </NextLink>
            </Text>
          </Row>
        ) as never
      )
    } else if (data.status === KTNDetailsStatusEnum.NOTELIGIBLEFORRENEWAL) {
      setClearKtnDescription(
        (
          <Row mx={[2, 6]} data-testid="ktn-card-not-eligible-for-renewal">
            <Text variant="body" textAlign="center" color="neutralsTextGray">
              Your Known Traveler Number (KTN) is valid until the expiration
              date above. You may renew your {TSA_PRECHECK_BRAND} membership
              online up to 6 months before your membership expiration date.
            </Text>
          </Row>
        ) as never
      )
    } else if (data.status === KTNDetailsStatusEnum.ELIGIBLEFORRENEWAL) {
      setClearKtnDescription(
        (
          <>
            <Row
              justifyContent="center"
              data-testid="ktn-card-eligible-for-renewal"
              width="100%"
              mt={[2, 2]}
            >
              <Alert
                defaultIcon
                backgroundColor="brandBlueWash"
                text={`You are eligible to renew ${TSA_PRECHECK_BRAND}`}
              />
            </Row>
            <Row justifyContent="center" mt={[2, 6]}>
              <ContinueButton
                data-testid="clear-renewal-eligible-continue"
                id="clear-renewal-eligible-continue"
                onClick={() => {
                  const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
                  dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
                    payload: {
                      message:
                        'User began renewal eligibility search for CLEAR enrollment from KTN Lookup results',
                    },
                    metadata: {
                      kpi: KPI.USER_INTERACTION,
                      xCLEARCorrelationInfo,
                      event: DiscreteEvent.RENEWAL_CLICK_FROM_KTN_LOOKUP_CLEAR,
                    },
                  })
                  router.push('/renewal/eligibility/eligibility')
                }}
                text={`Renew ${TSA_PRECHECK_BRAND}`}
              />
            </Row>
          </>
        ) as never
      )
    } else if (data.status === KTNDetailsStatusEnum.COMPLETED) {
      setClearKtnDescription(
        (
          <>
            <Row data-testid="ktn-card-completed">
              <Text
                variant="body"
                textAlign="center"
                color="neutralsTextGray"
                mx={[2, 6]}
                p={[3, 6]}
              >
                Your Known Traveler Number (KTN) has been issued. Known Traveler
                Numbers (KTNs) are automatically generated to accommodate
                personal preferences.
              </Text>
            </Row>
            <Row mx={[2, 6]}>
              <Text variant="body" textAlign="center" color="neutralsTextGray">
                For information on how to use your KTN please see this “How do I
                use my Known Traveler Number?” FAQ{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="https://www.tsa.gov/precheck/faq"
                  target="_blank"
                >
                  here
                </Link>
                .
              </Text>
            </Row>
          </>
        ) as never
      )
    }
  }, [data, isClearInProgressRenewal])

  return (
    <>
      {!!data.ktn && !!data.expirationDate ? (
        <StatusCardDetails
          data-testid="clear-new-enrollment-status-card"
          content1="Known Traveler Number (KTN)"
          value1={data.ktn}
          content2="Expiration Date"
          value2={dateToString(data.expirationDate)}
        />
      ) : null}
      {clearKtnDescription}
    </>
  )
}

function NonClearRenewalIneligible({
  otherProviderNotice,
}: {
  otherProviderNotice: string
}) {
  return (
    <>
      <Row
        alignItems="center"
        data-testid="non-clear-renewal-ineligible-row"
        mx={0}
        mt={[2, 4]}
      >
        <Alert
          backgroundColor="brandBlueWash"
          defaultIcon
          text={otherProviderNotice}
        />
      </Row>
    </>
  )
}

function NonClearRenewalEligible({
  otherProviderNotice,
}: {
  otherProviderNotice: string
}) {
  return (
    <>
      <Row data-testid="non-clear-renewal-ineligible-row" mx={0} mt={[2, 2]}>
        <Text variant="body" textAlign="center" color="neutralsTextGray">
          {otherProviderNotice}
        </Text>
      </Row>
      <Row
        data-testid="non-clear-renewal-eligible-row"
        width="100%"
        mx={0}
        mt={[2, 6]}
      >
        <Alert
          defaultIcon
          backgroundColor="brandBlueWash"
          text="While we are unable to return your KTN, you are eligible for renewal."
        />
      </Row>
      <Row justifyContent="center" mx={0} mt={[2, 6]}>
        <ContinueButton
          data-testid="non-clear-renewal-eligible-continue"
          id="non-clear-renewal-eligible-continue"
          onClick={() => {
            const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
            dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
              payload: {
                message:
                  'User began renewal eligibility search for non-CLEAR enrollment from KTN Lookup results',
              },
              metadata: {
                kpi: KPI.USER_INTERACTION,
                xCLEARCorrelationInfo,
                event: DiscreteEvent.RENEWAL_CLICK_FROM_KTN_LOOKUP_NON_CLEAR,
              },
            })
            router.push('/renewal/eligibility/eligibility')
          }}
          text={`Renew ${TSA_PRECHECK_BRAND}`}
        />
      </Row>
    </>
  )
}

export default KtnCard
